import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSoundNotification } from './useSoundNotification';
import { useToast } from '@/hooks/use-toast';

interface Order {
  id: string;
  status: string;
  customer_name: string | null;
  created_at: string;
}

export const useOrderPolling = (restaurantId: string) => {
  const [isPolling, setIsPolling] = useState(false);
  const [lastOrderCount, setLastOrderCount] = useState<number>(0);
  const lastOrderIdsRef = useRef<Set<string>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wakeLockRef = useRef<any>(null);
  const isActiveRef = useRef<boolean>(true);
  const { playNotificationSound, initializeAudio } = useSoundNotification();
  const { toast } = useToast();

  const checkForNewOrders = useCallback(async () => {
    if (!restaurantId) return;

    try {
      console.log('🔍 Checking for new orders at:', new Date().toISOString());
      
      const { data: currentOrders, error } = await supabase
        .from('orders')
        .select('id, status, customer_name, created_at')
        .eq('restaurant_id', restaurantId)
        .or('status.eq.pending,status.eq.waiting_for_payment')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching orders:', error);
        return;
      }

      const currentOrderIds = new Set(currentOrders?.map(order => order.id) || []);
      const currentOrderCount = currentOrders?.length || 0;
      
      console.log(`📊 Found ${currentOrderCount} active orders`);

      // Check for truly new orders
      const newOrderIds = Array.from(currentOrderIds).filter(id => 
        !lastOrderIdsRef.current.has(id)
      );

      if (newOrderIds.length > 0) {
        console.log(`🔔 FOUND ${newOrderIds.length} NEW ORDERS!`);
        
        // Play sound and show notifications
        await playNotificationSound();
        
        const newOrders = currentOrders?.filter(order => newOrderIds.includes(order.id)) || [];
        newOrders.forEach(order => {
          toast({
            title: "🔔 New Order Received!",
            description: `Order #${order.id.slice(0, 8)} from ${order.customer_name || 'Customer'}`,
            duration: 8000,
          });
        });

        // Vibrate on mobile if supported
        if ('navigator' in window && 'vibrate' in navigator) {
          navigator.vibrate([200, 100, 200]);
        }
      }

      // Update tracking
      lastOrderIdsRef.current = currentOrderIds;
      setLastOrderCount(currentOrderCount);

    } catch (error) {
      console.error('❌ Error in checkForNewOrders:', error);
    }
  }, [restaurantId, playNotificationSound, toast]);

  // More aggressive mobile polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    
    console.log('🚀 Starting aggressive order polling...');
    setIsPolling(true);
    
    // Very frequent polling for mobile - 3 seconds
    const pollInterval = 3000;
    
    intervalRef.current = setInterval(() => {
      if (isActiveRef.current) {
        checkForNewOrders();
      }
    }, pollInterval);
  }, [checkForNewOrders]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      console.log('❌ Stopping order polling');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Request wake lock more aggressively
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
        console.log('🔒 Wake lock acquired');
        
        wakeLockRef.current.addEventListener('release', () => {
          console.log('🔓 Wake lock released - re-requesting...');
          // Try to re-acquire wake lock
          setTimeout(requestWakeLock, 1000);
        });
      } catch (err) {
        console.log('⚠️ Wake lock failed:', err);
      }
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!restaurantId) return;

    console.log('🎵 Initializing aggressive order polling for:', restaurantId);
    
    initializeAudio();

    // Initial check
    const initialCheck = async () => {
      try {
        const { data: initialOrders, error } = await supabase
          .from('orders')
          .select('id, status, customer_name, created_at')
          .eq('restaurant_id', restaurantId)
          .or('status.eq.pending,status.eq.waiting_for_payment')
          .order('created_at', { ascending: false });

        if (!error && initialOrders) {
          const initialOrderIds = new Set(initialOrders.map(order => order.id));
          lastOrderIdsRef.current = initialOrderIds;
          setLastOrderCount(initialOrders.length);
          console.log('✅ Initial orders loaded:', initialOrders.length);
        }
      } catch (error) {
        console.error('❌ Error in initial check:', error);
      }
    };

    initialCheck();
    startPolling();
    requestWakeLock();

    // Handle visibility changes - keep polling even when hidden on mobile
    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden;
      console.log('👁️ Page visibility:', document.hidden ? 'hidden' : 'visible');
      
      if (!document.hidden) {
        // Immediate check when page becomes visible
        checkForNewOrders();
        // Ensure polling is active
        if (!intervalRef.current) {
          startPolling();
        }
      }
      // Don't stop polling when hidden - mobile needs continuous monitoring
    };

    // Handle focus events
    const handleFocus = () => {
      console.log('🎯 Window focused');
      isActiveRef.current = true;
      checkForNewOrders();
      if (!intervalRef.current) {
        startPolling();
      }
      requestWakeLock();
    };

    const handleBlur = () => {
      console.log('😴 Window blurred - keeping polling active');
      // Keep isActiveRef.current = true for mobile
      // Mobile apps need continuous monitoring
    };

    // Prevent mobile from sleeping
    const preventSleep = () => {
      if (!intervalRef.current) {
        startPolling();
      }
      requestWakeLock();
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('touchstart', preventSleep, { passive: true });
    window.addEventListener('click', preventSleep, { passive: true });

    // Keep alive mechanism for mobile
    const keepAliveInterval = setInterval(() => {
      if (!intervalRef.current) {
        console.log('🔄 Restarting polling (keep alive)');
        startPolling();
      }
    }, 10000); // Check every 10 seconds

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('touchstart', preventSleep);
      window.removeEventListener('click', preventSleep);
      clearInterval(keepAliveInterval);
      stopPolling();
      releaseWakeLock();
    };
  }, [restaurantId, checkForNewOrders, initializeAudio, startPolling, stopPolling, requestWakeLock, releaseWakeLock]);

  const manualCheck = useCallback(() => {
    console.log('🔄 Manual order check triggered');
    checkForNewOrders();
  }, [checkForNewOrders]);

  return {
    isPolling: !!intervalRef.current,
    manualCheck,
    lastOrderCount
  };
};
